import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Backdrop,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  CloudUpload as CloudUploadIcon,
  Warning as WarningIcon,
  FolderOpen as FolderIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import {
  useGetBackupsQuery,
  useGenerateBackupMutation,
  useDeleteBackupMutation,
  useRestoreBackupMutation,
  useRestoreUploadedBackupMutation,
} from '../../../store/api/backupApi';
import { format } from 'date-fns';

const DatabaseBackup = () => {
  const { data: backups = [], isLoading, refetch } = useGetBackupsQuery();
  const [generateBackup, { isLoading: isGenerating }] = useGenerateBackupMutation();
  const [deleteBackup, { isLoading: isDeleting }] = useDeleteBackupMutation();
  const [restoreBackup, { isLoading: isRestoringFile }] = useRestoreBackupMutation();
  const [restoreUploadedBackup, { isLoading: isRestoringUploaded }] = useRestoreUploadedBackupMutation();

  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Restore states
  const [restoreTarget, setRestoreTarget] = useState(null); // 'uploaded' or filename string
  const [confirmText, setConfirmText] = useState('');
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isRestoring = isRestoringFile || isRestoringUploaded;

  const handleGenerateBackup = async () => {
    try {
      await generateBackup().unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to generate backup:', error);
      alert('Failed to generate backup: ' + (error.data?.message || error.message));
    }
  };

  const handleDelete = async (filename) => {
    if (window.confirm('Are you sure you want to delete this backup?')) {
      try {
        await deleteBackup(filename).unwrap();
        refetch();
      } catch (error) {
        console.error('Failed to delete backup:', error);
        alert('Failed to delete backup: ' + (error.data?.message || error.message));
      }
    }
  };

  const handleDownload = (filename) => {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.29.142:5001/api';
    
    fetch(`${baseUrl}/backup/download/${filename}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) throw new Error('Download failed');
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    })
    .catch(error => {
      console.error('Failed to download:', error);
      alert('Failed to download backup');
    });
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.sql')) {
        setSelectedFile(file);
      } else {
        alert('Please upload only .sql backup files');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.name.endsWith('.sql')) {
        setSelectedFile(file);
      } else {
        alert('Please upload only .sql backup files');
      }
    }
  };

  const triggerRestoreFile = (filename) => {
    setRestoreTarget(filename);
    setConfirmText('');
    setConfirmCheck(false);
    setShowConfirmModal(true);
  };

  const triggerRestoreUploaded = () => {
    if (!selectedFile) return;
    setRestoreTarget('uploaded');
    setConfirmText('');
    setConfirmCheck(false);
    setShowConfirmModal(true);
  };

  const handleExecuteRestore = async () => {
    if (!confirmCheck || confirmText !== 'RESTORE') {
      return;
    }

    setShowConfirmModal(false);

    try {
      if (restoreTarget === 'uploaded') {
        const formData = new FormData();
        formData.append('file', selectedFile);
        await restoreUploadedBackup(formData).unwrap();
        setSelectedFile(null);
      } else {
        await restoreBackup(restoreTarget).unwrap();
      }
      alert('Database restored successfully!');
      refetch();
    } catch (error) {
      console.error('Restore failed:', error);
      alert('Database restore failed: ' + (error.data?.message || error.message || 'Unknown error'));
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  // Stats
  const totalSize = backups.reduce((sum, b) => sum + (b.size || 0), 0);
  const lastBackup = backups.length > 0 ? backups[0].createdAt : null;

  return (
    <Box sx={{ p: 4, maxWidth: 1400, margin: '0 auto' }}>
      {/* Backdrop for Restoring State */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 9999, display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}
        open={isRestoring}
      >
        <CircularProgress color="inherit" size={64} />
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Restoring Database...
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 450, px: 2, opacity: 0.85 }}>
          Please wait. Do not close this browser tab, refresh the page, or navigate away. The database is being reset and updated.
        </Typography>
      </Backdrop>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Database Backup & Restore
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Create snapshots of your database and restore them in case of data issues.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
          onClick={handleGenerateBackup}
          disabled={isGenerating || isRestoring}
          sx={{
            px: 3,
            py: 1.2,
            boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              boxShadow: '0 6px 20px 0 rgba(0,118,255,0.48)'
            }
          }}
        >
          {isGenerating ? 'Generating Backup...' : 'Generate New Backup'}
        </Button>
      </Box>

      {/* Metrics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{xs:12,sm:4}}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Box sx={{ p: 1.5, mr: 2, borderRadius: 2, backgroundColor: 'primary.light', color: 'primary.main', display: 'flex' }}>
                <FolderIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Backups Saved
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {backups.length}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{xs:12,sm:4}}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Box sx={{ p: 1.5, mr: 2, borderRadius: 2, backgroundColor: 'success.light', color: 'success.main', display: 'flex' }}>
                <FileIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Backups Size
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {formatBytes(totalSize)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{xs:12,sm:4}}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Box sx={{ p: 1.5, mr: 2, borderRadius: 2, backgroundColor: 'warning.light', color: 'warning.main', display: 'flex' }}>
                <BackupIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Last Backup Date
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, fontSize: lastBackup ? '1.1rem' : '1.25rem' }}>
                  {lastBackup ? format(new Date(lastBackup), 'PPpp') : 'Never'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Layout */}
      <Grid container spacing={4}>
        {/* Backups List */}
        <Grid size={{xs:12,md:8}}>
          <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden', borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Available Server Backups
              </Typography>
              <Button size="small" variant="outlined" onClick={refetch} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                Refresh List
              </Button>
            </Box>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Filename</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Size</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'background.default', pr: 3 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={36} />
                      </TableCell>
                    </TableRow>
                  ) : backups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          No database backups found on the server.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    backups.map((backup) => (
                      <TableRow key={backup.filename} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>{backup.filename}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {format(new Date(backup.createdAt), 'PPpp')}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{formatBytes(backup.size)}</TableCell>
                        <TableCell align="right" sx={{ pr: 2 }}>
                          <Tooltip title="Download SQL File">
                            <IconButton color="primary" onClick={() => handleDownload(backup.filename)} disabled={isRestoring}>
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restore Database">
                            <IconButton color="warning" onClick={() => triggerRestoreFile(backup.filename)} disabled={isRestoring}>
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Backup">
                            <IconButton color="error" onClick={() => handleDelete(backup.filename)} disabled={isDeleting || isRestoring}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Upload SQL File & Risks */}
        <Grid size={{xs:12,md:4}}>
          <Grid container spacing={3}>
            {/* Upload Zone */}
            <Grid size={{xs:12}}>
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Upload & Restore Local SQL Backup
                </Typography>
                
                <Box
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current.click()}
                  sx={{
                    border: '2px dashed',
                    borderColor: dragActive ? 'primary.main' : 'rgba(0, 0, 0, 0.15)',
                    backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.04)' : 'background.default',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.01)',
                    }
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".sql"
                    onChange={handleFileChange}
                  />
                  <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1.5, opacity: 0.8 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Drag & drop database .sql file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse your files
                  </Typography>
                </Box>

                {selectedFile && (
                  <Box sx={{ mt: 3, p: 2, borderRadius: 2, backgroundColor: 'action.hover', border: '1px solid rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <FileIcon color="primary" />
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatBytes(selectedFile.size)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
                        variant="contained"
                        color="warning"
                        fullWidth
                        size="medium"
                        startIcon={<RestoreIcon />}
                        onClick={triggerRestoreUploaded}
                        disabled={isRestoring}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Restore From File
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="medium"
                        onClick={() => setSelectedFile(null)}
                        disabled={isRestoring}
                        sx={{ textTransform: 'none' }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Risk Warnings */}
            <Grid size={{xs:12}}>
              <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(239, 83, 80, 0.2)', backgroundColor: 'rgba(239, 83, 80, 0.02)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, color: 'error.main' }}>
                  <WarningIcon />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Critical Information
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 1.5 }}>
                  Restoring a backup drops all current database tables and overwrites them with the selected snapshot.
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  Any transactions, items, or records added after the backup date will be permanently deleted. Ensure you create a fresh backup of the current database before running a restore if you have active changes.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={showConfirmModal}
        onClose={() => !isRestoring && setShowConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'warning.main', fontWeight: 700 }}>
          <WarningIcon /> Confirm Database Restore
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Warning: This action will completely overwrite the active database!
            </Typography>
            All existing tables, data, and configurations will be dropped and replaced. This operation cannot be undone.
          </Alert>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            {restoreTarget === 'uploaded' ? (
              <>You are about to restore the database from the uploaded local file: <strong>{selectedFile?.name}</strong>.</>
            ) : (
              <>You are about to restore the database from the server backup: <strong>{restoreTarget}</strong>.</>
            )}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmCheck}
                onChange={(e) => setConfirmCheck(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 550, userSelect: 'none' }}>
                I understand that this action drops all active database tables.
              </Typography>
            }
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Type RESTORE to confirm"
            variant="outlined"
            placeholder="RESTORE"
            disabled={!confirmCheck}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            sx={{ mb: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowConfirmModal(false)}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExecuteRestore}
            variant="contained"
            color="warning"
            disabled={!confirmCheck || confirmText !== 'RESTORE'}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              boxShadow: confirmCheck && confirmText === 'RESTORE' ? '0 4px 14px 0 rgba(237, 108, 2, 0.4)' : 'none'
            }}
          >
            Confirm & Execute Restore
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseBackup;
